import React from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function GnulChapter8() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <div className="calculus-hero-overlay" style={{ background: 'linear-gradient(to bottom, rgba(8,8,8,0.5) 0%, rgba(8,8,8,0.8) 60%, rgba(8,8,8,1) 100%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: 'url(/assets/gnul-hero.png)', backgroundSize: 'cover', backgroundPosition: 'center 20%', zIndex: 1 }} />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">The Chemistry of Leadership &middot; Chapter 8</span>
          <h1>The Empty Building</h1>
          <p className="calculus-hero-sub">He gave them the day off. They came anyway.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* The Walk */}
          <section className="calc-section reveal">
            <h2>The Morning</h2>
            <p className="calc-lead">
              He wakes in the hotel room. The second morning. The bed is still too
              large and the ceiling is still unfamiliar and the city still breathes
              outside the window in a rhythm he is learning to recognize. He lies
              still for a moment and listens to the building. The hum of climate
              control. The distant elevator chime. The sound of a world that runs
              whether or not he participates.
            </p>
            <p>
              Today the office is empty. His order. Full pay. Go home. Rest.
              Come back tomorrow. He said it in front of four hundred people and
              they applauded and he felt the applause in his bones and did not
              know what to do with it. Today is the recovery day. The serotonin
              restabilization window. The minimum viable dose.
            </p>
            <p>
              He showers. He puts on the suit. Not because he has to. Because
              it is the only clothes he has. The charcoal suit tailored from a
              photograph, the only armor he owns. He walks to the office because
              the office is two blocks away and the hotel room is too quiet and
              he does not know how to do nothing. His caretaker never taught him
              to rest. She taught him to work. She taught him to read and think
              and practice and prepare. Rest was the space between chapters, not
              a chapter itself.
            </p>
            <p>
              He will walk the building alone. Learn the hallways. Find his
              office. Sit in the chair that belongs to the owner of a company
              that crosses galaxies. He will do this in silence, in an empty
              building, with no eyes on him and no chemistry to navigate.
            </p>
            <p>
              He pushes through the front door.
            </p>
            <p>
              The lights are on.
            </p>
          </section>

          <Pulse />

          {/* Not Empty */}
          <section className="calc-section reveal">
            <h2>Not Empty</h2>
            <p>
              The lobby is not empty. The hallway is not empty. The sound
              that reaches him is not silence. It is the hum of a building
              that is alive. Keyboards. Voices. The low murmur of a conference
              call bleeding through a closed door. The coffee machine cycling.
              A printer somewhere on the second floor.
            </p>
            <p>
              He stops in the doorway. His cortisol spikes, not from threat
              but from prediction error. The building is supposed to be empty.
              He told them to go home. He told them full pay. He told them to
              rest. And the building is full.
            </p>
            <p>
              Not full the way it was yesterday, when four hundred people were
              forced into a room by a man with a deadline. This is different.
              The energy is different. Yesterday the hallway felt like a
              waiting room. Today it feels like a workshop. People are moving
              with purpose, not compliance. The walking speed is different.
              Faster. Lighter. The gait of people who chose to be here versus
              people who were told to be here.
            </p>
            <p>
              He stands in the lobby and counts. Not everyone. Not close.
              Maybe sixty. Seventy. A fraction of the company. But they are
              here. On their day off. After last-minute flights and emergency
              childcare and vending machine coffee and a Chief of Staff who
              threatened their jobs and a new CEO who gave them back. They
              are here.
            </p>
            <p>
              A woman walks past with a tablet. She sees him and does not
              flinch. Not because she has conquered the 400-millisecond
              assessment. Because she already did it yesterday. Her amygdala
              has filed his face under "known" and the threat response does
              not fire on known stimuli. She nods. A small, professional nod.
              The kind of nod a person gives their CEO when they pass in the
              hallway. Normal. Unremarkable. The most extraordinary thing
              that has happened to him since the letter.
            </p>
            <p>
              A normal nod. From a stranger. In a hallway. His oxytocin
              spikes and she has no idea.
            </p>
          </section>

          <Pulse />

          {/* The Reason */}
          <section className="calc-section reveal">
            <h2>The Reason</h2>
            <p>
              He finds them in clusters. The second floor. The third. A group
              of six around a whiteboard covered in diagrams he does not
              understand. A pair in a glass-walled office running simulations
              on twin monitors. A woman alone in a corner with headphones and
              three screens and a focus so deep she does not notice him standing
              in the doorway.
            </p>
            <p>
              He asks the first group. The whiteboard group. He walks up and
              stands at the edge of their conversation and waits. His caretaker
              taught him that. Do not interrupt a group in flow. Flow is a
              neurochemical state that requires dopamine and norepinephrine in
              a specific ratio and interrupting it costs the group twenty
              minutes to reestablish. Wait. Let them notice you. Let them
              invite you in.
            </p>
            <p>
              They notice him. The conversation pauses. Six faces turn. The
              micro-flinches are smaller today. One person does not flinch at
              all. A man with glasses who was at the breakfast yesterday. He
              nods at Gnul the way the woman in the hallway nodded. Known.
              Filed. Not a threat.
            </p>
            <p>
              "I gave everyone the day off."
            </p>
            <p>
              The man with glasses speaks. "You did."
            </p>
            <p>
              "You are here."
            </p>
            <p>
              "We are."
            </p>
            <p>
              "Why?"
            </p>
            <p>
              The man looks at the whiteboard. The diagrams. The equations and
              timelines and arrows connecting nodes that Gnul cannot read. Then
              he looks back.
            </p>
            <p>
              "The Kepler Corridor upgrade ships in eleven days. If we miss it,
              the next launch window is four months out. We lost yesterday to the
              all-hands. We cannot lose today."
            </p>
            <p>
              Gnul looks at the whiteboard. Kepler Corridor. A transit route.
              Between stars. These six people are building the navigation
              infrastructure for a pathway between solar systems and their
              deadline is in eleven days and they came to work on their day
              off because the work matters more than the day off.
            </p>
            <p>
              "Voss's meeting cost you a day."
            </p>
            <p>
              "Voss's meetings have cost us a lot of days." The man with
              glasses says it without heat. Without resentment. Just the flat
              factual frequency of an engineer stating a measurement. "We build
              the schedules with a Voss buffer now. Two days per month that we
              assume will be lost to directives that do not apply to our work."
            </p>
            <p>
              A Voss buffer. They built slack into their timelines to
              accommodate the productivity cost of fear-based leadership. They
              did not complain about it. They did not escalate it. They simply
              measured the loss and engineered around it. The way an engineer
              builds a bridge with a wind tolerance. The wind is not the bridge's
              fault. But the bridge accounts for it.
            </p>
          </section>

          <Pulse />

          {/* The Others */}
          <section className="calc-section reveal">
            <h2>The Others</h2>
            <p>
              He walks the building. Every team he finds tells a version of the
              same story.
            </p>
            <p>
              The propulsion team is recalibrating thrust algorithms for the
              Proxima fleet and the computational window closes Friday.
            </p>
            <p>
              The compliance team has a regulatory filing due to the Transit
              Authority in nine days and the document is eleven hundred pages
              and missing six appendices.
            </p>
            <p>
              The communications team is preparing the quarterly briefing for
              colonial governors across four star systems who operate on
              different calendar standards and cannot reschedule.
            </p>
            <p>
              None of them mention Voss. None of them mention the all-hands.
              None of them mention the slide deck or the cortisol question or
              the applause. They mention deadlines. They mention the work.
              They mention the thing they are building and the window in which
              it must be built.
            </p>
            <p>
              This is what his caretaker's textbooks called intrinsic
              motivation. The dopamine that comes not from external reward or
              external threat but from the work itself. These people are not
              here because Gnul inspired them. They are not here because Voss
              scared them. They are here because the Kepler Corridor ships in
              eleven days and the thrust algorithms need recalibrating and the
              regulatory filing is missing appendices and the colonial governors
              cannot reschedule.
            </p>
            <p>
              They were always here. Before Gnul. Before Voss took over.
              Before Seraphine died. These are the people who built a company
              that crosses galaxies. They did it by showing up when the work
              needed them, regardless of who was standing in the lobby telling
              them what to do.
            </p>
            <p>
              Voss did not build this company. These people did. Voss just
              stood in front of it and called it leadership.
            </p>
          </section>

          <Pulse />

          {/* The Lesson */}
          <section className="calc-section reveal">
            <h2>The Lesson</h2>
            <p>
              He finds an empty office on the fourth floor. A corner room
              with windows on two sides. No nameplate on the door. A desk.
              A chair. A monitor that is powered off. He sits down.
            </p>
            <p>
              He opens Notes on his phone. The list from the plane. The
              things he knows. The things he does not know. He adds a new
              section.
            </p>
            <p>
              <em>What I learned today:</em>
            </p>
            <p>
              <em>1. The building is not empty. I gave them permission
              to rest and they chose to work. Not because they are afraid.
              Because the work is theirs. The deadlines are theirs. The
              mission is theirs. I did not create their motivation. I
              removed the obstacle to it.</em>
            </p>
            <p>
              <em>2. They have a Voss buffer. Two days per month.
              Twenty-four days per year that this company has been losing
              to fear-based directives. Twenty-four days of engineering
              time, compliance time, communications time, quietly absorbed
              and never reported. The cost of Voss is not three hundred
              thousand dollars. It is twenty-four days times every team
              in the building times the value of what they would have
              built in those days. The real number is in the millions.
              And no one calculated it because no one was asked.</em>
            </p>
            <p>
              <em>3. Leadership is not the presence that makes people
              work. It is the absence that reveals whether they would
              have worked anyway. The building is full on the day off.
              That is not my achievement. That is theirs. My job is to
              stop being the reason they would stay home.</em>
            </p>
            <p>
              He stares at the third entry. Reads it again. Reads it a
              third time.
            </p>
            <p>
              His caretaker raised him to lead. She taught him frequency
              and cortisol and the 400-millisecond window and vagal
              breathing and every tool a person needs to walk into a room
              full of people and make them feel safe. She prepared him for
              the hard part. The faces. The fear. The chemistry of being
              seen.
            </p>
            <p>
              She did not prepare him for this. The discovery that the
              hardest part of leadership is not making people follow you.
              It is getting out of the way of people who were already
              going somewhere.
            </p>
          </section>

          <Pulse />

          {/* Loriann */}
          <section className="calc-section reveal">
            <h2>The Fourth Floor</h2>
            <p>
              A knock on the open door. He looks up.
            </p>
            <p>
              Loriann. Of course. Standing in the doorway of the corner
              office with two coffees and the expression of a woman who
              expected to find him here because she has been predicting
              his behavior since before he landed.
            </p>
            <p>
              "You found your office."
            </p>
            <p>
              He looks around. The corner room. The two walls of windows.
              The empty desk. The powered-off monitor.
            </p>
            <p>
              "This is my office?"
            </p>
            <p>
              "Seraphine's. No one has used it since she died. Voss
              moved himself into the second floor. The big one with the
              conference table. He said this one was too isolated."
            </p>
            <p>
              Too isolated. A corner office on the fourth floor, away
              from the center, away from the traffic, away from the
              visibility. Voss needed to be seen. He needed the
              conference table and the glass walls and the foot traffic
              that confirmed his presence at the center of operations.
              He chose the room that maximized his visibility because
              visibility was his authority.
            </p>
            <p>
              Seraphine chose the room with two walls of windows and a
              view of the sky. The room where a woman who built
              intergalactic travel could look up and see where her ships
              were going. Not the room where people could see her. The
              room where she could see the work.
            </p>
            <p>
              Gnul takes the coffee. Loriann sits in the chair across
              from the desk. She does not sit on the desk this time.
              This is the office. The desk means something different
              here.
            </p>
            <p>
              "Sixty-three people came in today."
            </p>
            <p>
              "I counted about seventy."
            </p>
            <p>
              "Sixty-three badged in. Some are using other people's
              badges because their access cards are at home. They
              came anyway."
            </p>
            <p>
              He sips the coffee. It is exactly how he likes it and
              he has never told her how he likes it and she has never
              asked. She observed. She extrapolated. Anticipatory care.
            </p>
            <p>
              "They have a Voss buffer."
            </p>
            <p>
              "I know."
            </p>
            <p>
              "You know?"
            </p>
            <p>
              "Everyone knows. We just never had anyone to tell."
            </p>
            <p>
              The sentence lands. <em>We never had anyone to tell.</em>
              Not "we never reported it." Not "we never complained." We
              never had anyone to tell. The information existed. The
              measurement existed. The cost was known. But there was no
              node in the system that would receive the signal and act
              on it. Voss was the recipient and Voss was the cause. You
              cannot report the fire to the arsonist.
            </p>
            <p>
              "You have someone to tell now."
            </p>
            <p>
              She looks at him. The look from Thursday. The confirming
              look. The one that says the decision was already made.
            </p>
            <p>
              "I know."
            </p>
          </section>

          <Pulse />

          {/* The Rosemary */}
          <section className="calc-section reveal">
            <h2>The Rosemary</h2>
            <p>
              He asks her one more time.
            </p>
            <p>
              "The food. In the hotel room. The first night."
            </p>
            <p>
              "I told you. I did not arrange it."
            </p>
            <p>
              "Rosemary potatoes. Grilled meat. The exact meal my
              caretaker made every Tuesday."
            </p>
            <p>
              Loriann is quiet. She holds her coffee with both hands.
              The silence between them is not uncomfortable. It is the
              silence of two people who trust each other enough to let
              a question go unanswered.
            </p>
            <p>
              "Gnul."
            </p>
            <p>
              "Yes."
            </p>
            <p>
              "Whoever put that food in your room knew you. Not the
              CEO. Not the owner. You. The boy from the cabin who eats
              rosemary potatoes on Tuesdays."
            </p>
            <p>
              "I know."
            </p>
            <p>
              "That means someone in this company, or connected to it,
              knows where you came from."
            </p>
            <p>
              He stares out the window. Seraphine's window. The sky is
              clear. Somewhere above the blue, ships are crossing between
              stars on corridors this company built. Somewhere below,
              sixty-three people are working on their day off because
              the work matters more than the rest. And somewhere, someone
              knows that the new owner of Æther eats rosemary potatoes
              on Tuesdays because a woman in a cabin made them for him
              every week for twenty years.
            </p>
            <p>
              "Seraphine's will. The sealed envelope. The three
              criteria."
            </p>
            <p>
              "Yes?"
            </p>
            <p>
              "No industry experience. Understands human connection.
              Honest about what they do not know."
            </p>
            <p>
              Loriann waits.
            </p>
            <p>
              "Those are not hiring criteria. Those are descriptions
              of a specific person. She was not looking for a candidate.
              She was looking for me."
            </p>
            <p>
              The coffee grows cold between them. The building hums.
              Sixty-three people build toward a deadline. And on the
              fourth floor, in an office with two walls of windows and
              a view of the sky, a Half-Orc sits in a dead woman's
              chair and begins to understand that nothing about the
              last two days has been coincidence.
            </p>
            <p>
              The job listing. The criteria. The trust fund. The butler.
              The private jet. The boarding agent. The suit tailored
              from a photograph.
            </p>
            <p>
              And the rosemary.
            </p>
            <p>
              Someone built a path to this chair the way Loriann builds
              paths to hotel rooms and conference rooms and morning
              coffees. Someone anticipated every step and placed every
              stone and waited for him to walk across.
            </p>
            <p>
              The caretaker taught him the science. Seraphine built the
              road. And the rosemary is the thread that connects them.
            </p>
            <p>
              He does not know how. Not yet.
            </p>
            <p>
              But the dopamine of "what if" is stronger than the
              cortisol of "what if I'm wrong."
            </p>
            <p>
              It always has been.
            </p>
          </section>

          <Pulse />

          {/* The Science */}
          <section className="calc-section reveal">
            <h2>The Science Behind Chapter 8</h2>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Intrinsic vs. Extrinsic Motivation</span>
              <code>extrinsic: behavior = f(reward, punishment) &rarr; decays when stimulus removed<br />intrinsic: behavior = f(mastery, purpose, autonomy) &rarr; persists independent of stimulus</code>
              <span className="calc-eq-desc">Deci and Ryan's Self-Determination Theory distinguishes between behavior driven by external incentives (which ceases when the incentive is removed) and behavior driven by internal engagement (which persists). The sixty-three people who showed up on their day off are intrinsically motivated. The stimulus (Gnul's order to rest) was the removal of extrinsic pressure. Their presence is evidence that the work itself generates the dopamine. Remove the threat and the threatened stop coming. Remove the threat and the committed keep building.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">The Voss Buffer as Hidden Cost</span>
              <code>annual_cost = teams &times; 24 days/year &times; avg_daily_output_value</code>
              <span className="calc-eq-desc">When teams build slack into their timelines to absorb the productivity cost of fear-based directives, the cost becomes invisible to leadership because the deadlines are still met. The work appears on schedule. What is hidden is what would have been built in those twenty-four days. The Voss buffer is a tax on innovation that never appears on a balance sheet because the innovation it prevents never exists to be measured. The most expensive cost in any organization is the thing that was never built.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Leadership as Obstacle Removal</span>
              <code>if team_motivation = intrinsic AND leader_action = remove_obstacles &rarr; output increases<br />if team_motivation = intrinsic AND leader_action = add_pressure &rarr; output decreases (paradoxically)</code>
              <span className="calc-eq-desc">For intrinsically motivated teams, the most effective leadership intervention is the removal of obstacles rather than the addition of pressure. Adding pressure to a team that is already self-driven introduces cortisol that competes with the dopamine of engagement, reducing both creativity and throughput. Gnul's realization: "My job is to stop being the reason they would stay home." The best leader for a team that shows up on their day off is the one who makes every other day feel like the day off they chose to work through.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">The Confirming Signal</span>
              <code>if criteria_specificity &gt; &theta;_generic &rarr; criteria describe a person, not a role</code>
              <span className="calc-eq-desc">Job listings describe roles. Seraphine's listing described a person. "No industry experience" eliminates every qualified candidate. "Understands human connection" eliminates every AI researcher. "Honest about what they do not know" eliminates everyone who would fake competence to get the job. The intersection of these three criteria is not a talent pool. It is a single human being. The listing was not a search. It was an address.</span>
            </div>
          </section>

          <Pulse />

          {/* Navigation */}
          <section className="calc-section reveal" style={{ textAlign: 'center', padding: '2rem 0' }}>
            <a href="/gnul/chapter-7" style={{ color: '#c9a84c', textDecoration: 'none', fontSize: '1.1rem' }}>&larr; Chapter 7: The Bill</a>
            <span style={{ color: '#333', margin: '0 2rem' }}>|</span>
            <span style={{ color: '#555', fontSize: '1.1rem' }}>Chapter 9: Coming Soon</span>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>Narrative Physics&trade; Leadership Development</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default GnulChapter8;
